import { catchError, tap, take, takeWhile, skipWhile, finalize, switchMap, map, mergeMap } from 'rxjs/operators';
import { OnInit, Component, AfterContentInit, OnDestroy } from '@angular/core';

import { Store } from '@ngrx/store';
import { AppState } from '../../store/AppState';
import {
  AttributeResultDTO,
  AttributeValueDTO,
  DocumentDTO,
  I18nData,
  TaskDTO
} from '../../api/plyca-dtos';
import { TaskService } from '../task.service';
import { generateRandomIdentifier, UIService } from '../../ui.service';
import { HeaderActionsService } from '../../components/common/sub-header/header-actions.service';
import { PlycaDataService } from '../../../services/plyca.service';
import { Configuration } from '../../conf/app.constants';
import { from, EMPTY, Observable, timer, forkJoin, throwError, iif, of } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { WaitLoadingService } from '../../components/common/waitLoading/wait-loading.service';
import * as lodash from 'lodash';
import { BaseComponent } from '../../components/common/baseComponent/base.component';
import { ActionDefinitionDTO, ActionResultDTO } from '../../api/plyca-dtos-v2';
import { PaginationDTO } from '../../api/PaginationModel';
import { UserService } from '../../user.service';
import { ConfirmationService } from 'primeng-lts/api';
import { DialogService, DynamicDialogConfig, DynamicDialogRef } from 'primeng-lts/dynamicdialog';
import { OverlayPanel } from 'primeng-lts/overlaypanel';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { MultiDocumentViewerComponent } from '../../documents/documentViewer/multiDocumentViewer.component';
import { HelpService } from '../../components/common/help/help.service';

enum TaskStatus {
  PENDING,
  RUNNING,
  OK,
  ERROR
};

enum ProcessStatus {
  PENDING,
  RUNNING,
  FINISHED,
  ABORTED
}

export class MultiTaskColumnConfig {
  type: 'description' | 'arrivalDate' | 'endDate' | 'attributeColumn' | 'viewDocumentsTask';
  attributeColumn: string;
  descriptionKey: string;
  class: string;
}

export class MultiTaskActionColumnConfig {
  type: 'viewDocumentsTask'
}

export enum MultiTaskConfigType {
  ATTRIBUTE,
  FILTER
}

export class MultiTaskConfig {
  // enumerado con el valor por defecto a 'attribute'
  type: MultiTaskConfigType = MultiTaskConfigType.ATTRIBUTE;
  // acción a ejecutar
  action: string;
  columns: MultiTaskColumnConfig[];
}

export class MultiTaskAttributeConfig extends MultiTaskConfig {
  type = MultiTaskConfigType.ATTRIBUTE;
  // propiedades de configuracion cuando type = 'attribute'
  attributeId: string;
  attributeTaskIdColumn: string;
  parameters: string[];
  useAttributeOrder: boolean;
  // propiedad de utilidad para guardar resultados de ejecucion del atributo
  attributeResult: AttributeResultDTO;
}

export class MultiTaskFilterConfig extends MultiTaskConfig {
  type = MultiTaskConfigType.FILTER;
  // propiedades de configuracion cuando type = 'attribute'
  filterFn: string;
}

@Component({
  selector: 'multiTaskAction',
  templateUrl: 'multiTaskAction.template.html',
  providers: [DialogService, UIService]
})
export class MultiTaskActionComponent extends BaseComponent implements OnInit, AfterContentInit, OnDestroy {

  // CONSTANTES PARA GESTIONAR EL ESTADO DE LA FIRMA DE CADA TAREA
  readonly MESSAGE_PROPERTY = '_stateMessage';
  readonly STATUS_PROPERTY = '_actionStatus';
  readonly ATTRIBUTE_PROPERTY = '_attribute';
  readonly SELECTED_PROPERTY = '_selected';
  readonly TaskStatus = TaskStatus;

  // estado del proceso
  readonly ProcessStatus = ProcessStatus;
  public processStatus = ProcessStatus.PENDING;

  public progressPercentage = 0;
  public startedTasks = 0;
  public executedTasks = 0;
  public errorTasks = 0;

  /**
   * tareas seleccionadas por el usuario para procesar
   */
  public task2Process: Array<TaskDTO> = [];
  /**
   * tareas que muestra la tabla. Paso 1 -> todas las de firma, Paso 2 solo seleccionadas
   */
  public tableTasks: Array<TaskDTO> = [];
  public windowHash: string;
  public allSelected = false;
  public actionMessage: string;
  // paso actual
  public currentStep = 1;
  public loaded = false;

  /**
   * Parametros de configuracion
   */
  public taskConfig: MultiTaskConfig;
  private actionId: string;
  public descriptionClass: string;
  public arrivalDateClass: string;
  public endDateClass: string;
  public columnsConfig: MultiTaskColumnConfig[];
  public viewDocumentsTask: MultiTaskColumnConfig;

  constructor(
    private store: Store<AppState>,
    protected taskService: TaskService,
    protected userService: UserService,
    protected plycaService: PlycaDataService,
    protected configuration: Configuration,
    protected dialogConfig: DynamicDialogConfig,
    protected dialogService: DialogService,
    protected ref: DynamicDialogRef,
    private headerActionsService: HeaderActionsService,
    protected translateService: TranslateService,
    protected waitService: WaitLoadingService,
    private uiService: UIService,
    protected confirmationService: ConfirmationService,
    protected plycaErrorHandler: PlycaErrorHandler,
    protected helpService: HelpService
  ) {
    super();
  }


  ngOnInit(): void {
    // validacion parametros obligatorios
    if ((this.dialogConfig?.data?.taskConfig?.attributeId && this.dialogConfig?.data?.taskConfig?.attributeTaskIdColumn)
      || this.dialogConfig.data.taskConfig.action) {

      this.taskConfig = this.dialogConfig.data.taskConfig;
      if (this.taskConfig && this.taskConfig.columns) {
        let columnConfig = this.taskConfig.columns.find(config => config.type === 'description');
        if (columnConfig) {
          this.descriptionClass = columnConfig.class;
        }
        columnConfig = this.taskConfig.columns.find(config => config.type === 'arrivalDate');
        if (columnConfig) {
          this.arrivalDateClass = columnConfig.class;
        }
        columnConfig = this.taskConfig.columns.find(config => config.type === 'endDate');
        if (columnConfig) {
          this.endDateClass = columnConfig.class;
        }
        this.columnsConfig = this.taskConfig.columns.filter(config => config.type === 'attributeColumn');
        this.viewDocumentsTask = this.taskConfig.columns.find(config => config.type === 'viewDocumentsTask');
      }
      // this.attributeId = this.taskConfig.attributeId;
      // this.attributeTaskIdColumn = this.taskConfig.attributeTaskIdColumn;
      // this.parameterColumns = this.taskConfig.parameters;
      this.subscribe(
        // consultar atributo y acci�n en paralelo, y a continuaci�n cargar lista de tareas
        forkJoin([
          iif(() => this.isAttributeTaskConfig(this.taskConfig),
            // carga por atributo si es de tipo atributo
            this.initFromAttribute(),
            // carga por filtro si es de tipo filtro
            this.initFromFilter()
          ).pipe(
            tap((taskToLoad: Array<TaskDTO>) => {
              this.tableTasks = taskToLoad;
              // cargar tabla
              this.task2Process.splice(0);
              this.allSelected = false;
              this.progressPercentage = 0;
              this.currentStep = 1;
              this.loaded = true;
              this.waitService.cancelWaiting();
            })
          ),
          // consultar accion
          this.plycaService.query<ActionDefinitionDTO>(this.configuration.ACTIONDEFINICION_SERVICE_URL, {
            actionObjType: 'TASK',
            id: this.taskConfig.action
          }).pipe(
            catchError((error: any) => this.showErrorAndClose(error,
              this.translateService.instant('MultiTaskAction.Errors.Action'))),
            tap((result: PaginationDTO<ActionDefinitionDTO>) => {
              if (!result || !result.content || result.content.length === 0) {
                this.subscribe(this.showErrorAndClose(undefined, this.translateService.instant('MultiTaskAction.Errors.NoAction')));
              } else {
                this.actionId = result.content[0].identifier;
                this.subscribe(this.helpService.showContextHelpObservable(this.taskConfig.action));
              }
            }))
        ])
      );
    } else {
      const message = this.translateService.instant('MultiTaskAction.Errors.Param');
      console.error(message, this.dialogConfig);
      this.showErrorAndClose(undefined, message);
    }
  }

  private isAttributeTaskConfig(config: MultiTaskConfig): boolean {
    return config.type === undefined || config.type === MultiTaskConfigType.ATTRIBUTE
  }

  ngOnDestroy(): void {
    this.dialogConfig = null;
    this.dialogService = null;
    this.headerActionsService.clearWindowActions(this.windowHash);
    super.ngOnDestroy();
  }

  ngAfterContentInit(): void {
    this.windowHash = generateRandomIdentifier();
    this.headerActionsService.clearWindowActions(this.windowHash);
    // acci�n firmar
    this.subscribe(this.headerActionsService.addWindowAction(
      'Actions.execute',
      'fas fa-bolt',
      'btn-primary',
      function () {
        return this.currentStep === 1;
      }.bind(this),
      this.windowHash
    ), () => {
      if (!this.task2Process.length) {
        this.uiService.showMessage('Seleccione las tareas a procesar', new I18nData('General.info'));
      } else {
        this.processTasks();
      }
    });
    // acci�n cancelar
    // this.subscribe(this.headerActionsService.addWindowAction(
    //   'General.cancel',
    //   'fas fa-stop',
    //   'btn-secondary',
    //   function () {
    //     return this.processStatus === ProcessStatus.RUNNING;
    //   }.bind(this),
    //   this.windowHash
    // ), () => {
    //   this.subscribe(this.cancelProcess());
    // });
    // acci�n cerrar ventana
    this.subscribe(this.headerActionsService
      .addWindowAction(
        'General.close',
        'fas fa-times',
        'btn-outline-secondary',
        true,
        this.windowHash
      ),
      () => {
        if (this.processStatus === ProcessStatus.RUNNING) {
          let dialogRef = this.uiService.showMessage(this.translateService.instant('MultiTaskAction.Alert.Close'));
          this.subscribe(dialogRef.onClose, () => this.close());
        } else {
          this.close();
        }
      });
  }


  private initFromAttribute(): Observable<Array<TaskDTO>> {
    return this.plycaService.getAttributeValue((this.taskConfig as MultiTaskAttributeConfig).attributeId).pipe(
      catchError((error: any) => this.showErrorAndClose(error,
        this.translateService.instant('MultiTaskAction.Errors.Attribute'))),
      tap((result: AttributeResultDTO) => {
        (this.taskConfig as MultiTaskAttributeConfig).attributeResult = result;
      }),
      switchMap((result: AttributeResultDTO) => this.loadTasksFromAttribute())
    );
  }

  private initFromFilter(): Observable<Array<TaskDTO>> {
    return this.store.select('pendingTasks').pipe(
      take(1),
      map((tasks: Array<TaskDTO>) => {
        let filteredTasks = new Array<TaskDTO>();
        const taskFilterConfig = this.taskConfig as MultiTaskFilterConfig;
        return tasks.filter((task: TaskDTO) => {
          let valid = false;
          try {
            valid = this.evalFunction(taskFilterConfig.filterFn, task);
          } catch (erro: any) {
            console.error('Error al aplicar filtro', taskFilterConfig.filterFn, ' a la tarea', task);
          }
          return valid;
        });
      })
    );
  }

  private evalFunction(expression: string, target: any): boolean {
    let tmpFunc = new Function('return ' + expression);
    return tmpFunc.apply(target);
  }

  private loadTasksFromAttribute(): Observable<Array<TaskDTO>> {
    this.waitService.startWaiting();
    const taskAttrConfig = this.taskConfig as MultiTaskAttributeConfig;
    let useAttributeOrder = this.taskConfig && taskAttrConfig.attributeTaskIdColumn && taskAttrConfig.useAttributeOrder;
    this.tableTasks.splice(0);
    // tslint:disable-next-line:triple-equals
    return iif(() => taskAttrConfig.attributeResult && taskAttrConfig.attributeResult.values != undefined,
      this.store.select('pendingTasks').pipe(
        take(1),
        map((tasks: Array<TaskDTO>) => {
          let tableTasks: Array<TaskDTO>;
          // cargar datos de la tabla
          if (useAttributeOrder) {
            tableTasks = this.loadTasksOrderByAttribute(tasks);
          } else {
            tableTasks = this.loadTasksOrderByArrival(tasks);
          }
          return tableTasks;
          // this.task2Process.splice(0);
          // this.allSelected = false;
          // this.progressPercentage = 0;
          // this.currentStep = 1;
          // this.loaded = true;
          // this.waitService.cancelWaiting();
        })
      ),
      of(new Array<TaskDTO>()));
  }

  /**
   * Carga de tareas en el orden del atributo
   *
   * @param tasks
   * @returns
   */
  private loadTasksOrderByAttribute(tasks: TaskDTO[]): Array<TaskDTO> {
    let tableTasks = new Array<TaskDTO>();
    const taskAttrConfig = this.taskConfig as MultiTaskAttributeConfig;
    if (taskAttrConfig.attributeResult && taskAttrConfig.attributeResult.values) {
      taskAttrConfig.attributeResult.values.forEach((value: AttributeValueDTO) => {
        let storeTask = tasks.filter((task: TaskDTO) => !this.taskService.isLockedTask(task))
          .find((task: TaskDTO) => task.identifier === value.columns[taskAttrConfig.attributeTaskIdColumn]['value']);
        if (storeTask) {
          let _task = lodash.cloneDeep(storeTask);
          _task[this.STATUS_PROPERTY] = TaskStatus.PENDING;
          _task[this.ATTRIBUTE_PROPERTY] = value;
          tableTasks.push(_task);
        }
      });
    }
    return tableTasks;
  }

  /**
   * Carga de tareas en el orden del almacen de tareas
   *
   * @param tasks
   * @returns
   */
  private loadTasksOrderByArrival(tasks: TaskDTO[]): Array<TaskDTO> {
    let tableTasks = new Array<TaskDTO>();
    tasks.filter((task: TaskDTO) => !this.taskService.isLockedTask(task))
      .forEach((task: TaskDTO) => {
        let attributeValues = this.getTaskAttribute(task.identifier);
        if (attributeValues !== undefined) {
          let _task = lodash.cloneDeep(task);
          _task[this.STATUS_PROPERTY] = TaskStatus.PENDING;
          _task[this.ATTRIBUTE_PROPERTY] = attributeValues;
          console.log('Estado de la tarea con id', task.identifier, ' cambiado a PENDING');
          tableTasks.push(_task);
        }
      });
    return tableTasks;
  }

  private showErrorAndClose(error: any, message: string): Observable<any> {
    this.subscribe(this.uiService.showMessage(message, new I18nData('General.error')).onClose,
      () => this.close());
    return throwError(error || message);
  }

  private getTaskAttribute(taskId: string): AttributeValueDTO {
    const taskAttrConfig = this.taskConfig as MultiTaskAttributeConfig;
    return taskAttrConfig.attributeResult.values.find((value: AttributeValueDTO) => {
      let found = false;
      if (value.columns) {
        const columnValue = value.columns[taskAttrConfig.attributeTaskIdColumn];
        found = columnValue && columnValue.value === taskId;
      }
      return found;
    });
  }

  public close() {
    if (this.processStatus === ProcessStatus.RUNNING) {
      this.subscribe(this.cancelProcess().pipe(
        finalize(() => this.internalClose())
      ));
    } else {
      this.internalClose();
    }
  }

  private internalClose(): void {
    this.ref.close({ taskUpdated: this.executedTasks + this.errorTasks > 0 });
  }


  public getStatusIcon(task: TaskDTO): string {
    if (task[this.STATUS_PROPERTY] === TaskStatus.RUNNING) {
      return 'fa fa-spinner loading-spin fa-lg';
    } else if (task[this.STATUS_PROPERTY] === TaskStatus.OK) {
      return 'fas fa-check text-success  fa-lg';
    } else if (task[this.STATUS_PROPERTY] === TaskStatus.ERROR) {
      return 'fas fa-exclamation-circle text-danger fa-lg';
    }
  }

  public showActionMessage(task: TaskDTO, event: Event, overlaypanel: OverlayPanel): void {
    this.actionMessage = task[this.MESSAGE_PROPERTY];
    overlaypanel.appendTo = 'body';
    if (this.actionMessage) {
      overlaypanel.show(event);
    }
  }

  public getProcessMessage(): string {
    let message: string;
    switch (this.processStatus) {
      case ProcessStatus.PENDING: {
        message = this.translateService.instant('MultiTaskAction.ProcessStatus.Pending');
        break;
      }
      case ProcessStatus.RUNNING: {
        message = this.translateService.instant('MultiTaskAction.ProcessStatus.Running');
        break;
      }
      case ProcessStatus.FINISHED: {
        if (this.errorTasks) {
          message = this.translateService.instant('MultiTaskAction.ProcessStatus.FinishERR');
        } else {
          message = this.translateService.instant('MultiTaskAction.ProcessStatus.FinishOK');
        }
        break;
      }
      case ProcessStatus.ABORTED: {
        message = this.translateService.instant('MultiTaskAction.ProcessStatus.Aborted');
        break;
      }
    }
    return message;
  }


  private processTasks(): void {
    if (this.task2Process.length > 0) {
      console.log('iniciando procesamiento de tareas');
      this.currentStep = 2;
      this.executedTasks = 0;
      this.errorTasks = 0;
      this.startedTasks = 0;
      this.processStatus = ProcessStatus.RUNNING;
      this.subscribe(from(this.task2Process).pipe(
        takeWhile(() => this.processStatus !== ProcessStatus.ABORTED),
        mergeMap((task: TaskDTO, index: number) => this.processTask(task, index))
      ));
    }
  }

  private processTask(task: TaskDTO, index: number) {
    let taskIdentifier = task.identifier;
    console.log('Estado de la tarea con id', taskIdentifier, ' cambiado a RUNNING');
    task[this.STATUS_PROPERTY] = TaskStatus.RUNNING
    this.startedTasks++;
    let parameters = '';
    if (this.isAttributeTaskConfig(this.taskConfig)) {
      const taskAttrConfig = this.taskConfig as MultiTaskAttributeConfig;
      if (taskAttrConfig.parameters) {
        const taskAttr = this.getTaskAttribute(task.identifier);
        let separator = '';
        taskAttrConfig.parameters.forEach(paramColumn => {
          let attrValue = taskAttr.columns[paramColumn];
          if (attrValue && attrValue.value) {
            parameters += separator + paramColumn + '=' + attrValue.value;
            separator = '&';
          }
        });
      }
    }
    return timer(250).pipe(
      takeWhile(() => this.processStatus !== ProcessStatus.ABORTED),
      mergeMap(() => this.plycaService.runAction(this.actionId, taskIdentifier, parameters)),
      catchError(err => {
        let errorMsg: string;
        if (err && err.status === 404) {
          errorMsg = this.translateService.instant('MultiTaskAction.Errors.NotFound');
        } else {
          errorMsg = this.plycaErrorHandler.getErrorMessage(err, this.translateService.instant('MultiTaskAction.Errors.Exec'));
        }
        console.error('Error al ejecutar la tarea con id', taskIdentifier, err);
        this.markTaskError(task, errorMsg);
        // devolvemos EMPTY para no pasar por el siguiente operador del pipe e ir al finally
        return EMPTY;
      }),
      tap((result: ActionResultDTO) => {
        if (result.identifier === 'OK') {
          console.log('Tarea ejecutada correctamente con id', taskIdentifier);
          this.executedTasks++;
          this.updateProgress();
          task[this.STATUS_PROPERTY] = TaskStatus.OK
          console.log('Estado de la tarea con id', task.identifier, ' cambiado a OK');
        } else {
          this.markTaskError(task, result.description);
        }
      })
    );
  }

  private updateProgress() {
    this.progressPercentage = Math.round(((this.executedTasks + this.errorTasks) / this.task2Process.length) * 100);
    if ((this.executedTasks + this.errorTasks) === this.task2Process.length) {
      this.processStatus = ProcessStatus.FINISHED;
    }
  }

  private markTaskError(task: TaskDTO, message: string): void {
    // guardo una copia en la tabla para no actualizar el objeto de tareas compartido por la aplicacion
    task[this.STATUS_PROPERTY] = TaskStatus.ERROR;
    console.log('Estado de la tarea con id', task.identifier, ' cambiado a ERROR');
    task[this.MESSAGE_PROPERTY] = message;
    this.errorTasks++;
    this.updateProgress();
  }

  private cancelProcess(): Observable<any> {
    this.waitService.startWaiting(this.translateService.instant('MultiTaskAction.Messages.WaitFinish'));
    this.processStatus = ProcessStatus.ABORTED;
    // consultar cada 1000 si han finalizado todas las peticiones enviadas y devolver solo en ese caso
    return timer(0, 1000).pipe(
      skipWhile(() => (this.startedTasks !== this.errorTasks + this.executedTasks)),
      tap(() => {
        this.waitService.cancelWaiting();
        // quitar marca de ejecutando a tareas restantes
        this.tableTasks.filter(task => task[this.STATUS_PROPERTY] === TaskStatus.RUNNING)
          .forEach(task => task[this.STATUS_PROPERTY] = undefined);
      })
    );
  }

  public selectAll(): void {
    // borramos selecciones anteriores
    this.task2Process.splice(0);
    this.tableTasks.forEach(task => this.selectTask(task, this.allSelected));
  }

  public selectTask(task: TaskDTO, selected: boolean): void {
    task[this.SELECTED_PROPERTY] = selected;
    if (selected) {
      this.task2Process.push(task);
    } else {
      this.task2Process = this.task2Process.filter(innerTask => innerTask !== task);
      this.allSelected = false;
    }
  }
  public viewDocument(task: TaskDTO): void {
    this.waitService.startWaiting();
    this.subscribe(
      this.taskService.lockTask(task.identifier, false, false).pipe(
        catchError(error => {
          console.error(error);
          this.plycaErrorHandler.showServerError(error);
          //this.uiService.showMessage(this.translateService.instant('No ha sido posible bloquear la tarea'));
          return throwError(error);
        }),
        mergeMap((openedTask: TaskDTO) => {
          let documents = openedTask?.work?.documents
          if (!documents || documents.length === 0) {
            this.waitService.cancelWaiting();
            this.uiService.showMessage(this.translateService.instant('La tarea no tiene documentos'));
            this.waitService.startWaiting();
            documents = null;
          }
          return this.taskService.unlockTask(openedTask, true).pipe(
            catchError(error => {
              this.plycaErrorHandler.showServerError(error);
              return throwError(error);
            }),
            finalize(() => {
              if (documents) {
                this.openDocument(documents);
              } else {
                this.waitService.cancelWaiting();
              }
            })
          );
        })
      )
    );
  }

  private openDocument(documents: DocumentDTO[]) {
    // let config: DynamicDialogConfig = {
    //   header: doc.name,
    //   width: '97%',
    //   styleClass: 'searchStyle'
    // };
    // config.data = {
    //   'id': doc.identifier
    // }
    // this.waitService.startWaiting();
    // let modal = this.dialogService.open(documentViewerComponent, config);
    // this.subscribe(modal.onClose);
    let config: DynamicDialogConfig = {
      // TODO i18n
      header: 'Detalle del documento',
      width: '97%',
      styleClass: 'searchStyle'
    };
    config.data = {
      documentIdentifiers: documents.map((doc: DocumentDTO) => doc.identifier)
    }
    this.waitService.startWaiting();
    this.dialogService.open(MultiDocumentViewerComponent, config);
  }

}
